/**
 * What a tax invoice adds up to. One rule, one place.
 *
 * The invoice service used to work this out inline, and it got it wrong in two ways at once:
 * it added ten per cent ON TOP of the price, and the price it was handed was the acquisition
 * figure rather than the agreed sale price. A wholesale price in this business is
 * GST-INCLUSIVE — that is the assumption the margin scheme, the GST register and every
 * report already run on (see lib/gstTreatment, which takes one eleventh OUT of a price).
 * An invoice that adds ten per cent on top of an inclusive price overstates the bill.
 *
 * So: the gross is what the buyer agreed to pay, GST included. The discount comes off that
 * gross. The GST is then extracted from what remains, at the rate on the invoice, and the
 * net price is the remainder. Net + GST equals the total exactly, by construction — the GST
 * is rounded once and the net is derived from it, so the three figures printed on the
 * invoice can never fail to add up.
 *
 * Winnie's section 5.1 asked for Discount, Net Price and Tax Rate on the sale record. They
 * are stored rather than derived at read time so that an invoice raised today still explains
 * itself if the rate ever changes.
 */

/** Australian GST, as a percentage. Stored per invoice, not assumed at read time. */
export const DEFAULT_TAX_RATE = 10

export interface InvoiceAmountsInput {
  /** What the buyer agreed to pay, GST included, before any discount. */
  gross: number
  /** Reduction agreed off the gross. Defaults to none. */
  discount?: number
  /** Percentage. Defaults to DEFAULT_TAX_RATE. Zero is legitimate, not an error. */
  taxRate?: number
}

export interface InvoiceAmounts {
  gross: number
  discount: number
  /** Gross less discount: what the buyer actually pays, GST included. */
  total: number
  /** The GST contained in the total. */
  gstAmount: number
  /** The total less the GST. */
  netPrice: number
  taxRate: number
}

const r2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100

export function invoiceAmounts(input: InvoiceAmountsInput): InvoiceAmounts {
  const gross = Math.max(0, r2(input.gross))
  const discount = Math.min(gross, Math.max(0, r2(input.discount ?? 0)))
  const taxRate = Math.max(0, Number(input.taxRate ?? DEFAULT_TAX_RATE) || 0)

  const total = r2(gross - discount)

  /**
   * Extracted, not added. At ten per cent this is total / 11, which is what gstComponent()
   * gives — the two agree deliberately.
   */
  const gstAmount = r2((total * taxRate) / (100 + taxRate))

  /** Derived from the rounded GST so the printed figures always reconcile. */
  const netPrice = r2(total - gstAmount)

  return { gross, discount, total, gstAmount, netPrice, taxRate }
}
